import Tooltip from 'threads5/Tooltip';
import Icon from 'threads5/Icon';
import IconButton from 'threads5/IconButton';
import styled from 'threads5/styles/styled';

import {
  Card,
  Disclaimer,
  Form,
  Label,
  Slider,
  HelperText,
} from 'src/components-v3/Calculator/Calculator.styled';
import BtnTertiary from 'src/components-v2/Inputs/Button/BtnTertiary';
import { Typography } from 'src/components-v2/DataDisplay/Typography';

// Re-export shared calculator styled components
export { Card, Disclaimer, Form, Label, Slider, HelperText };

// Desktop: show tooltip + icon; mobile: hide
const TooltipDesktop = styled('span')(({ theme }) => {
  return {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'inline-block',
    },
  };
});

// Mobile: show helper text beneath label; desktop: hide
const HelperTextMobile = styled('span')(({ theme }) => {
  return {
    display: 'block',
    marginTop: '4px',
    color: theme.palette['base/black/700'],
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  };
});

// Mortgage-specific: tooltip with info icon for form labels (desktop); on mobile, show helper text beneath label with P2 Regular
export const MortgageTooltip = ({ title }: { title: string }) => {
  return (
    <>
      <TooltipDesktop>
        <Tooltip
          title={title}
          placement='top'
          enterTouchDelay={0}
          sx={(theme) => {
            return {
              // MUI tooltip class name (not camelCase)
              /* eslint-disable-next-line @typescript-eslint/naming-convention */
              '.MuiTooltip-tooltip': {
                background:
                  theme.palette[
                    'web3/component/mortgage-calculator/tooltip/background'
                  ],
                color:
                  theme.palette[
                    'web3/component/mortgage-calculator/tooltip/text'
                  ],
                fontWeight: 500,
              },
            };
          }}
        >
          <IconButton
            appearance='ghost'
            intent='neutral'
            icon={(props) => {
              return <Icon name='Information' {...props} />;
            }}
            size='32'
            sx={{ mb: '-12px', borderRadius: '50% !important' }}
          >
            {title}
          </IconButton>
        </Tooltip>
      </TooltipDesktop>
      <HelperTextMobile>
        <Typography component='span' variant='P2 Regular' theme='web 3.0'>
          {title}
        </Typography>
      </HelperTextMobile>
    </>
  );
};

// Light mode icon gradient colors (from BtnTertiary SVG stops) – we always use these for the CTA so the icon reads on the gradient canvas
const TERTIARY_ICON_GRADIENT_LIGHT = {
  stop1: '#671877',
  stop2: '#2B2B82',
  stop3: '#043C67',
};

// Light mode hover token values (from theme componentsLight) – keep icon in light-mode style on hover in all themes
const TERTIARY_ICON_LIGHT_HOVER = {
  fillInteracted: '#111112',
  iconContainerInteractedFill: 'rgba(255, 255, 255, 0.50)',
};

// Styled CTA button: overlays the gradient canvas, fills wrapper
export const CardCtaButton = styled(BtnTertiary)(() => {
  return {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    transform: 'none',
    left: 0,
    top: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
    // Force light mode icon gradient in all themes so the icon contrasts on the gradient strip
    /* eslint-disable-next-line @typescript-eslint/naming-convention */
    '& svg stop:nth-child(3n+1)': {
      stopColor: TERTIARY_ICON_GRADIENT_LIGHT.stop1,
    },
    /* eslint-disable-next-line @typescript-eslint/naming-convention */
    '& svg stop:nth-child(3n+2)': {
      stopColor: TERTIARY_ICON_GRADIENT_LIGHT.stop2,
    },
    /* eslint-disable-next-line @typescript-eslint/naming-convention */
    '& svg stop:nth-child(3n+3)': {
      stopColor: TERTIARY_ICON_GRADIENT_LIGHT.stop3,
    },
    // Keep light mode icon styling on hover (gradient stop colors already forced above; override hover fill/stroke so dark theme doesn’t switch to white)
    /* eslint-disable-next-line @typescript-eslint/naming-convention */
    '&:hover svg': {
      boxShadow: 'none',
      fill: TERTIARY_ICON_LIGHT_HOVER.iconContainerInteractedFill,
      /* eslint-disable-next-line @typescript-eslint/naming-convention */
      '& path.arrow': {
        fill: TERTIARY_ICON_LIGHT_HOVER.fillInteracted,
      },
      /* eslint-disable-next-line @typescript-eslint/naming-convention */
      '& path.circle': {
        stroke: TERTIARY_ICON_LIGHT_HOVER.fillInteracted,
      },
    },
    /* eslint-disable-next-line @typescript-eslint/naming-convention */
    '&:focus-visible': {
      outline: 'none',
    },
  };
});

// Shimmer and glow colors for CTA hover effect
const SHIMMER_COLOR = 'rgba(255, 255, 255, 0.5)';
const HOVER_GLOW = 'rgba(66, 240, 205, 0.5)';

export const CanvasWrapper = styled('div')(({ theme }) => {
  return {
    position: 'relative',
    width: '100%',
    height: 56,
    overflow: 'hidden',
    borderRadius: 40,
    transition: 'transform 0.5s ease, box-shadow 0.5s ease',
    /* eslint-disable-next-line @typescript-eslint/naming-convention */
    '&:focus-within': {
      outlineWidth: '2px',
      outlineColor: theme.palette['web3/component/button/outline'],
      outlineStyle: 'solid',
      outlineOffset: '2px',
    },
    /* eslint-disable-next-line @typescript-eslint/naming-convention */
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-50%',
      left: '-50%',
      width: '200%',
      height: '200%',
      background: `linear-gradient(0deg, transparent, transparent 30%, ${SHIMMER_COLOR})`,
      transform: 'rotate(-45deg) translate(-50%, -50%)',
      transition: 'transform 0.28s ease, opacity 0.28s ease',
      opacity: 0,
      pointerEvents: 'none',
      zIndex: 1,
    },
    /* eslint-disable-next-line @typescript-eslint/naming-convention */
    '&:hover': {
      boxShadow: `0 0 20px ${HOVER_GLOW}`,
    },
    /* eslint-disable-next-line @typescript-eslint/naming-convention */
    '&:hover::before': {
      opacity: 1,
      transform: 'rotate(-45deg) translate(39%, 200%)',
    },
  };
});

export const Canvas = styled('canvas')(() => {
  return {
    width: 'calc(100% + 8px)',
    height: 239,
    borderRadius: 40,
    '--gradient-color-1': '#E4D3FA',
    '--gradient-color-2': '#8DD9FF',
    '--gradient-color-3': '#E4D3FA',
    '--gradient-color-4': '#B6F3F8',
    margin: '-97px 0px 0px -4px',
    filter: 'blur(4px)',
  };
});
